/**
 * Session appointment write path shared by the provider calendar modal and the
 * in-session (Zoom) scheduler.
 *
 * The arg builder is pure so both callers produce byte-identical
 * `ScheduledAppointmentArgs`; only the surrounding UI differs.
 */

import type { MedplumClient } from '@medplum/core';
import type { Appointment } from '@medplum/fhirtypes';
import type { AppointmentSessionType } from '@/pages/calendar/appointmentTypeCatalog';
import type { ScheduledAppointmentArgs } from '@/lib/scheduleAppointmentService';
import {
  createScheduledAppointment,
  updateScheduledAppointment,
} from '@/lib/scheduleAppointmentService';
import { saveCalendarAppointment } from './saveCalendarAppointment';
import { syncProviderAppointmentScheduleChange } from './providerAppointmentScheduleSync';
import { appointmentRequiresScheduleBot } from './signedNoteAppointmentLock';

/** The `AppointmentFormValues` subset a session appointment writes. */
export interface SessionAppointmentFormSlice {
  date: Date | null;
  startTime: string;
  endTime: string;
  /** Performing provider's Practitioner id. */
  provider: string;
  /** HealthcareService id from the session type catalog. */
  sessionType: string;
  /** Full refs (`Patient/<id>`, `RelatedPerson/<id>`). */
  sessionAttendees: string[];
  attendanceStatus: string;
  modality: 'in-person' | 'telehealth' | 'hybrid';
  /** Practice site id, or the `telehealth` sentinel. */
  siteId: string;
  officeForTelehealth: string;
  telehealthFromOffice: boolean;
  roomId: string | null;
  deviceIds: string[];
  recurring: string;
  description: string;
  videoLink: string;
  episodeOfCareId: string | null;
}

export interface SessionAppointmentContext {
  /** Logged-in practitioner; added as an attendee when booking for a colleague. */
  viewerPractitionerId: string;
  viewerPreferredDisplay?: string;
  organizationId?: string;
  patientId?: string;
  patientDisplay?: string;
  /** Preferred-only display for the selected performing provider. */
  providerPreferredDisplay?: string;
  /** Unfiltered catalog — id lookups must not use the episode-filtered list. */
  sessionTypes: AppointmentSessionType[];
  /** Ref → label for RelatedPerson attendees. */
  attendeeLabelByRef?: ReadonlyMap<string, string>;
  /** Existing room/device reservations that must be released even if the new value has none. */
  hasExistingScheduledResources?: boolean;
  /**
   * Editing a group session. Keeps the appointment classified as `group` and preserves
   * the cohort roster and per-participant attendance (owned by the Group, not this form).
   */
  groupSession?: boolean;
  /** Group co-facilitators written as ATND Practitioner participants (excludes the primary). */
  coFacilitators?: Array<{ id: string; preferredDisplay?: string }>;
}

export interface SessionAppointmentWrite {
  args: ScheduledAppointmentArgs;
  start: Date;
  end: Date;
  /** Room/device slots need the schedule-appointment bot's reservation handling. */
  usesScheduledResources: boolean;
}

function parseTimeLabel(label: string): { hour: number; minute: number } | null {
  const match = label.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return null;
  const period = match[3].toUpperCase();
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  return { hour, minute };
}

/** Local start/end instants from the form's date + 12h time labels. */
export function resolveSessionTimes(
  values: Pick<SessionAppointmentFormSlice, 'date' | 'startTime' | 'endTime'>,
): { start: Date; end: Date } | null {
  if (!values.date) return null;
  const startParsed = parseTimeLabel(values.startTime);
  const endParsed = parseTimeLabel(values.endTime);
  if (!startParsed || !endParsed) return null;
  const start = new Date(values.date);
  start.setHours(startParsed.hour, startParsed.minute, 0, 0);
  const end = new Date(values.date);
  end.setHours(endParsed.hour, endParsed.minute, 0, 0);
  if (end <= start) return null;
  return { start, end };
}

/**
 * Site / room / equipment actually persisted for the chosen modality.
 * Telehealth only reserves space when the provider takes the call from an office.
 */
export function resolveSessionSchedulingLocation(
  values: Pick<
    SessionAppointmentFormSlice,
    'modality' | 'siteId' | 'officeForTelehealth' | 'telehealthFromOffice' | 'roomId' | 'deviceIds'
  >,
): { siteId?: string; roomId?: string; deviceIds?: string[] } {
  const isTelehealth = values.modality === 'telehealth';
  const atPhysicalSite = !isTelehealth && values.siteId !== 'telehealth';

  const siteId = isTelehealth
    ? (values.telehealthFromOffice ? values.officeForTelehealth || undefined : undefined)
    : (atPhysicalSite ? values.siteId || undefined : undefined);

  const roomId = isTelehealth
    ? (values.telehealthFromOffice ? values.roomId ?? undefined : undefined)
    : (atPhysicalSite ? values.roomId ?? undefined : undefined);

  const deviceIds =
    (values.modality === 'in-person' || values.modality === 'hybrid')
      && atPhysicalSite
      && values.deviceIds.length > 0
      ? [...values.deviceIds]
      : undefined;

  return { siteId, roomId, deviceIds };
}

/**
 * Build the bot/FHIR write for a session appointment.
 * Returns `null` when the date/time is missing or inverted.
 */
export function buildSessionAppointmentArgs(
  values: SessionAppointmentFormSlice,
  context: SessionAppointmentContext,
): SessionAppointmentWrite | null {
  const times = resolveSessionTimes(values);
  if (!times) return null;

  const { siteId, roomId, deviceIds } = resolveSessionSchedulingLocation(values);
  const sessionType = context.sessionTypes.find((t) => t.id === values.sessionType);

  const practitionerId = values.provider || context.viewerPractitionerId;
  const additionalParticipants: NonNullable<ScheduledAppointmentArgs['additionalParticipants']> = [];

  /** The client is persisted as the SBJ participant via `patientId`. */
  for (const ref of values.sessionAttendees) {
    if (!ref.startsWith('RelatedPerson/')) continue;
    const id = ref.slice('RelatedPerson/'.length);
    if (!id) continue;
    const label = context.attendeeLabelByRef?.get(ref);
    additionalParticipants.push({
      resourceType: 'RelatedPerson',
      id,
      display: label && label !== ref ? label : undefined,
    });
  }

  /** Group co-facilitators ride along as extra ATND practitioners. */
  for (const co of context.coFacilitators ?? []) {
    if (!co.id || co.id === practitionerId) continue;
    if (additionalParticipants.some((p) => p.resourceType === 'Practitioner' && p.id === co.id)) {
      continue;
    }
    additionalParticipants.push({
      resourceType: 'Practitioner',
      id: co.id,
      display: co.preferredDisplay,
    });
  }

  const args: ScheduledAppointmentArgs = {
    start: times.start,
    end: times.end,
    calendarStatus: values.attendanceStatus || 'scheduled',
    practitionerId,
    practitionerDisplay: context.providerPreferredDisplay,
    siteId,
    organizationId: context.organizationId,
    roomId,
    deviceIds,
    /** A group session's clients are ATND roster members, not a single SBJ subject. */
    patientId: context.groupSession ? undefined : context.patientId,
    patientDisplay: context.groupSession ? undefined : context.patientDisplay,
    episodeOfCareId: values.episodeOfCareId ?? undefined,
    cptCode: sessionType?.cptCode,
    cptDisplay: sessionType?.billingTitle,
    sessionHealthcareServiceId: values.sessionType || undefined,
    sessionHealthcareServiceDisplay: sessionType?.description,
    appointmentCategory: context.groupSession ? 'group' : 'session',
    ...(context.groupSession ? { preserveExistingParticipants: true } : {}),
    modality: values.modality || 'in-person',
    description: values.description || undefined,
    videoLink: values.videoLink || undefined,
    recurringPattern: values.recurring || undefined,
    additionalParticipants:
      additionalParticipants.length > 0 ? additionalParticipants : undefined,
  };

  return {
    args,
    start: times.start,
    end: times.end,
    usesScheduledResources:
      Boolean(roomId)
      || (deviceIds?.length ?? 0) > 0
      || Boolean(context.hasExistingScheduledResources),
  };
}

const HUMAN_SCHEDULE_PARTICIPANT_TYPES = new Set(['Practitioner', 'Patient', 'RelatedPerson']);

/** Keep every human attendee the calendar already knows about, including RelatedPerson family/couples members. */
export function additionalParticipantsForScheduleWrite(
  additionalParticipants: Array<{ resourceType: string; id: string; display?: string }> | undefined,
  practitionerDisplayById?: Map<string, string>,
): ScheduledAppointmentArgs['additionalParticipants'] {
  if (!additionalParticipants?.length) return undefined;
  const next: NonNullable<ScheduledAppointmentArgs['additionalParticipants']> = [];
  for (const participant of additionalParticipants) {
    if (!participant.id || !HUMAN_SCHEDULE_PARTICIPANT_TYPES.has(participant.resourceType)) continue;
    if (participant.resourceType === 'Practitioner') {
      const display = practitionerDisplayById?.get(participant.id);
      next.push({
        resourceType: 'Practitioner',
        id: participant.id,
        ...(display ? { display } : participant.display ? { display: participant.display } : {}),
      });
      continue;
    }
    next.push({
      resourceType: participant.resourceType as 'Patient' | 'RelatedPerson',
      id: participant.id,
      ...(participant.display ? { display: participant.display } : {}),
    });
  }
  return next.length > 0 ? next : undefined;
}

export function sessionWriteUsesScheduleBot(args: {
  usesScheduledResources: boolean;
  hasLinkedClinicalNote: boolean;
}): boolean {
  return args.usesScheduledResources || args.hasLinkedClinicalNote;
}

/**
 * Route a scheduled write: the bot when rooms/devices are involved (it owns Slot
 * reservations) or when the appointment has a clinical note (it owns the signed-note
 * lock). Direct FHIR write otherwise.
 */
export async function saveScheduledAppointmentWrite(
  medplum: MedplumClient,
  write: SessionAppointmentWrite,
  options: { appointmentId?: string; viewerPractitionerId?: string },
): Promise<Appointment> {
  const { appointmentId } = options;
  const before = appointmentId
    ? await medplum.readResource('Appointment', appointmentId)
    : undefined;

  const hasLinkedClinicalNote = appointmentId
    ? await appointmentRequiresScheduleBot(medplum, appointmentId)
    : false;
  const useScheduleBot = sessionWriteUsesScheduleBot({
    usesScheduledResources: write.usesScheduledResources,
    hasLinkedClinicalNote,
  });

  let saved: Appointment;
  if (useScheduleBot) {
    saved = appointmentId
      ? await updateScheduledAppointment(medplum, appointmentId, write.args)
      : await createScheduledAppointment(medplum, write.args);
  } else {
    saved = await saveCalendarAppointment({ medplum, appointmentId, ...write.args });
  }

  if (before && appointmentId) {
    /** Re-read: the bot path rewrites participants and slots after the initial response. */
    const after = await medplum.readResource('Appointment', appointmentId);
    try {
      await syncProviderAppointmentScheduleChange(medplum, {
        before,
        after,
        calendarStatus: write.args.calendarStatus,
      });
    } catch (err) {
      console.warn('[SessionAppointment] Appointment schedule sync failed:', err);
    }
    return after;
  }
  return saved;
}
